<template>
    <div class="p-image-uploader">
        <div>Current image:</div>

        <div class="p-image-uploader__error" v-if="validationError">
            {{ validationErrorMessage }}
        </div>
        <div class="p-image-uploader__preview" v-else>
            <figure v-if="previewImage || selectedFile" style="margin: 0">
                <img class="p-image-uploader__preview__img" v-if="previewImage" :src="previewImage" alt="">

                <figcaption v-if="selectedFile" class="p-image-uploader__preview__info">
                    <h2 class="p-image-uploader__preview__info__name">{{ selectedFile.name }}</h2>
                    <p class="p-image-uploader__preview__info__details">{{ selectedFile.type }} - {{
                            selectedFileSize
                        }}</p>
                </figcaption>
            </figure>

            <p class="p-image-uploader__preview__no-image" v-else>
                <translate>No image selected</translate>
            </p>
        </div>

        <input ref="fileInput" type="file" style="display: none" @change="onFileChanged">

        <button @click="$refs.fileInput.click()" class="btn btn-secondary btn-sm" v-translate>Choose a file</button>
        <button v-if="selectedFile" @click="clearSelectedFile" class="btn btn-outline-secondary btn-sm ml-1">
            Restore
        </button>

        <PButton v-if="selectedFile && !hideUploadButton" :loading="loading" @click="upload"
                 class="btn btn-info btn-sm float-right">
            <translate>Upload</translate>
        </PButton>
        <PButton v-else-if="currentImage" :loading="loading" @click="clearCurrentImage"
                 class="btn btn-outline-warning btn-sm ml-1 float-right">
            <translate>Remove</translate>
        </PButton>

        <CModal title="Conferma eliminazione" :show.sync="showModal">
            <translate>Confirm the removal of the image?</translate>
            <template #footer>
                <CButton @click="showModal = false" color="secondary">
                    <translate>Cancel</translate>
                </CButton>
                <CButton @click="clearCurrentImageOnServer" color="primary">
                    <translate>Remove</translate>
                </CButton>
            </template>
        </CModal>
    </div>
</template>


<script>
/*
* Inspiration from https://www.youtube.com/watch?v=VqnJwh6E9ak
* */
import PicaServiceService from "@/services/pica_service.service"
import PButton from "@/components/PButton"
import Compressor from "compressorjs";

export default {
    name: "PImageUploader",

    components: {
        PButton
    },

    props: {
        picaServiceId: {
            type: Number
        },

        fieldName: {
            type: String,
            required: true
        },

        currentImage: {
            type: String
        },

        validationOptions: {
            type: Object,
            default: null
        },

        compressorOptions: {
            type: Object,
            default: null
        },

        hideUploadButton: {
            type: Boolean,
            default: false
        },

        avoidRemoveFromServer: {
            type: Boolean,
            default: false
        },

        endpoint: {
            type: String,
            default: 'images'
        },

        onUpload: {
            type: Function
        }
    },

    data() {
        return {
            selectedFile: null,
            selectedUrl: null,
            loading: false,
            acceptedFileTypesArray: null,
            validationError: false,
            validationErrorMessage: null,
            showModal: false
        }
    },

    mounted() {
        // TODO: gestire il caso di modifica della prop acceptedFileTypes con un watcher
        if (this.validationOptions && this.validationOptions.acceptedFileTypes && this.validationOptions.acceptedFileTypes.length > 0)
            this.acceptedFileTypesArray = this.validationOptions.acceptedFileTypes.split(/[\s,]+/)
    },

    computed: {
        previewImage() {
            if (this.selectedFile)
                return this.selectedUrl
            return this.currentImage
        },

        selectedFileSize() {
            if (this.selectedFile.size)
                return this.formatBytes(this.selectedFile.size)
            return null
        }
    },

    methods: {
        validate(file) {
            if (this.validationOptions.acceptedFileTypes && this.acceptedFileTypesArray !== null && !this.acceptedFileTypesArray.includes(file.type)) {
                this.validationError = true
                this.validationErrorMessage = this.$gettextInterpolate(
                    this.$gettext('Incorrect file format (accepted formats: %{acceptedFormats})'),
                    {acceptedFormats: this.validationOptions.acceptedFileTypes}
                )
            }
            if (!this.validationError && this.validationOptions.minWidth || this.validationOptions.minHeight) {
                let img = new Image()
                img.src = window.URL.createObjectURL(file)
                img.onload = () => {
                    if (img.width < this.validationOptions.minWidth || img.height < this.validationOptions.minHeight) {
                        this.validationError = true
                        this.validationErrorMessage = this.$gettextInterpolate(
                            this.$gettext('The minimum size of the image must be %{width} x %{height} px'),
                            {width: this.validationOptions.minWidth, height: this.validationOptions.minHeight}
                        )
                    }
                }
            }
        },

        onFileChanged(event) {
            this.validationError = false

            // TODO: add
            if (this.validationOptions) {
                this.validate(event.target.files[0])
                if (this.validationError) {
                    this.clearSelectedFile()
                    return false
                }
            }

            if (this.compressorOptions) {
                new Compressor(event.target.files[0], {
                    ...this.compressorOptions,
                    // drew: (context, canvas) => {
                    //   /* hook called after the image is drew into the canvas for compression; use it to retrieve the image res */
                    // },
                    success: (result) => {
                        this.selectedFile = result
                        this.selectedUrl = URL.createObjectURL(this.selectedFile)
                    },
                })
            } else {
                this.selectedFile = event.target.files[0]
                this.selectedUrl = URL.createObjectURL(this.selectedFile)
            }
        },

        onDefaultUpload() {
            /*
            Upload file, get it from this.selectedFile
            */
            console.log('onUpload', this.selectedFile)
            const fd = new FormData()
            fd.append(this.fieldName, this.selectedFile, this.selectedFile.name)
            this.loading = true
            PicaServiceService.uploadPicaServiceImages(this.picaServiceId, fd, {
                onUploadProgress: (progressEvent) => {
                    console.log(Math.round(progressEvent.loaded / progressEvent.total * 100) + '%')
                }
            }, this.endpoint)
                .then((response) => {
                    console.log('upload complete!', response)
                    this.loading = false
                    this.clearSelectedFile()
                    this.notifyMessage({text: this.$gettext('Correctly edited image'), messageType: 'success'})
                    this.$emit('upload-completed', response.data[this.fieldName])
                })
        },

        upload() {
            if (this.onUpload) {
                this.onUpload()
            } else {
                this.onDefaultUpload()
            }
        },

        clearSelectedFile() {
            this.$refs.fileInput.value = ''
            this.selectedFile = null
            this.selectedUrl = null
        },

        clearCurrentImageOnServer() {
            this.showModal = false

            const fd = new FormData()
            fd.append(this.fieldName, '')
            this.loading = true
            PicaServiceService.uploadPicaServiceImages(this.picaServiceId, fd, {}, this.endpoint)
                .then((response) => {
                    console.log('remove complete!', response)
                    this.loading = false
                    this.notifyMessage({text: this.$gettext('Image deleted'), messageType: 'success'})
                    this.$emit('image-removed', response.data[this.fieldName])
                })
        },

        clearCurrentImage() {
            if (this.avoidRemoveFromServer) {
                this.$emit('image-removed')
            } else {
                this.showModal = true
            }
        },

        formatBytes(bytes, decimals = 2) {
            if (bytes === 0) return '0 Bytes'

            const k = 1024
            const dm = decimals < 0 ? 0 : decimals
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

            const i = Math.floor(Math.log(bytes) / Math.log(k))

            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
        }
    }
}
</script>


<style scoped>
.p-image-uploader__error {
    background-color: #b11010;
    color: #fff;
    margin-bottom: 10px;
    padding: 6px;
}

.p-image-uploader__preview {
    background-color: #ebedef;
    margin-bottom: 10px;
}

.p-image-uploader__preview__no-image {
    margin: .4rem;
}

.p-image-uploader__preview__img {
    max-width: 100%;
}

.p-image-uploader__preview__info {
    padding: 6px;
}

.p-image-uploader__preview__info__name {
    margin: 0;
    font-size: .9rem;
}

.p-image-uploader__preview__info__details {
    margin-bottom: 0;
}
</style>